import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { switchMap, catchError, map, mergeMap, tap, first, filter } from 'rxjs/operators';
import { EMPTY, throwError } from 'rxjs';

import * as DataActions from './actions';
import { HandoverProtocolApiFacade } from '../handover-protocol-api.facade';
import { HandoverProtocolDataFacade } from '../handover-protocol-data.facade';
import { SnackbarService } from 'services/snackbar.service';
import { DocumentService } from 'services/document.service';
import { environment } from 'src/environments/environment';
import { DocumentsDataFacade } from "../../documents";
import { TranslationService } from "../../../i18n/TranslationService";
import { LocalHandoverProtocolService } from '../local-handover-protocol.service';
import { NetworkStatusService } from 'services/networking-status.service';
import { LOCAL_DOCUMENT_ID_PREFIX } from 'src/app/offline/local-database';

@Injectable()
export class HandoverProtocolEffects {
  public loadHandoverProtocols$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DataActions.LoadHandoverProtocols),
      switchMap(({ objectId }) =>
        this._hopApiFacade.loadHandoverProtocols$(objectId).pipe(
          switchMap((value) => [DataActions.HandoverProtocolsLoaded({ payload: value })]),
          catchError(() => [DataActions.HandoverProtocolsLoadingFailed()])
        )
      )
    )
  );

  public loadHandoverProtocolsByCustomer$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DataActions.LoadHandoverProtocolsByCustomer),
      switchMap(({ customerId }) =>
        this._hopApiFacade.loadHandoverProtocolsByCustomer$(customerId).pipe(
          switchMap((value) => [DataActions.HandoverProtocolsLoaded({ payload: value })]),
          catchError(() => [DataActions.HandoverProtocolsLoadingFailed()])
        )
      )
    )
  );

  public loadHandoverProtocolDetails$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DataActions.LoadHandoverProtocolDetails),
      switchMap(({ objectId, hopId }) =>
        this._hopApiFacade.loadHandoverProtocolDetails$(objectId, hopId).pipe(
          switchMap((value) => [DataActions.HandoverProtocolDetailsLoaded({ payload: value })]),
          catchError(() => [DataActions.HandoverProtocolDetailsLoadingFailed()])
        )
      )
    )
  );

  public loadLocalHandoverProtocolDetails$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DataActions.LoadLocalHandoverProtocolDetails),
      switchMap(({ id }) =>
        this._localHandoverProtocolService.get$(id).pipe(
          switchMap((value) => [DataActions.HandoverProtocolDetailsLoaded({ payload: value })]),
          catchError(() => [DataActions.HandoverProtocolDetailsLoadingFailed()])
        )
      )
    )
  );

  public saveHandoverProtocol$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DataActions.SaveHandoverProtocol),
      switchMap(({ handoverProtocol, isFinal, shouldPrint }) =>
        this._networkStatusService.isOnline$.pipe(
          first(),
          filter(Boolean),
          switchMap(() => {
            if (!handoverProtocol?.id || handoverProtocol?.id.startsWith(LOCAL_DOCUMENT_ID_PREFIX)) {
              handoverProtocol.id = undefined;
            }

            return this._hopApiFacade.saveHandoverProtocol$(handoverProtocol).pipe(
              switchMap((hop) => {
                if (isFinal) {
                  if (shouldPrint) {
                    this._hopDataFacade.showHandoverProtocolDocument(hop.objectId, hop.id);
                  }

                  this._router.navigateByUrl(`/objects/${hop.objectId}/handover-protocols`);

                  return [DataActions.SaveHandoverProtocolSucceded({ showToast: true })];
                }

                return [DataActions.SaveHandoverProtocolSucceded({ showToast: false })];
              }),
              catchError(() => [DataActions.SaveHandoverProtocolFailed({ showToast: isFinal ?? true })]),
            );
          }),
        ),
      ),
    )
  );

  public duplicateHandoverProtocol$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DataActions.DuplicateHandoverProtocol),
      switchMap(({ hopId, objectId }) =>
        this._hopApiFacade.duplicateHandoverProtocol$(hopId, objectId).pipe(
          switchMap(() => {
            const successMessage = this._translationService.instant('handover_protocol') + ' ' + this._translationService.instant('general.duplicated_successfully');
            this._snackbarService.showSnackbar(
              successMessage,
              'mat-primary',
              true
            );
            return [DataActions.DuplicateHandoverProtocolSuccess(), DataActions.LoadHandoverProtocols({ objectId })];
          }
          ),
          catchError(() => {
            const errorMessage = this._translationService.instant('handover_protocol') + ' ' + this._translationService.instant('general.duplicated_failure');
            this._snackbarService.showSnackbar(
              errorMessage,
              'mat-warn',
              true
            );
            return [DataActions.DuplicateHandoverProtocolFailed()];
          }
          ),
        ))
    ),
  );


  deleteHandoverProtocol$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DataActions.DeleteHandoverProtocol),
      mergeMap(({ hopId, objectId }) =>
        this._hopApiFacade
          .deleteHandoverProtocol$(hopId)
          .pipe(
            switchMap(() => {
              const successMessage = this._translationService.instant('handover_protocol') + ' ' + this._translationService.instant('general.deleted_successfully');
              this._snackbarService.showSnackbar(
                successMessage,
                'mat-primary',
                true
              );
              this._documentDataFacade.deleteHandoverProtocol(hopId)
              return [
                DataActions.DeleteHandoverProtocolSuccess({
                  hopId,
                }),
              ];
            }), catchError(() => {
              const errorMessage = this._translationService.instant('handover_protocol') + ' ' + this._translationService.instant('general.deleted_failure');
              this._snackbarService.showSnackbar(
                errorMessage,
                'mat-warn',
                true
              );
              return [DataActions.DeleteHandoverProtocolFailed({ hopId })];
            })
          )
      ),
    ),
  );

  public createAndUploadHandoverProtocolDocumentFailed$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DataActions.CreateAndUploadHandoverProtocolDocumentFailed),
      map(() => {
        const pdfCreationErrorMessage = this._translationService.instant('handover_protocol') + ' ' + this._translationService.instant('general.pdf_creation_failure');
        return this._snackbarService.showSnackbar(
          pdfCreationErrorMessage,
          'mat-warn',
          true
        )
      }
      )
    ),
    { dispatch: false }
  );

  public handoverProtocolCreationSucceded$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DataActions.SaveHandoverProtocolSucceded),
      tap(({ showToast }) => {
        if (showToast) {
          const successMessage = this._translationService.instant('handover_protocol') + ' ' + this._translationService.instant('general.saved_successfully');
          this._snackbarService.showSnackbar(
            successMessage,
            'mat-primary',
            true
          );
        }
      })
    ),
    { dispatch: false }
  );

  public handoverProtocolCreationFailed$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DataActions.SaveHandoverProtocolFailed),
      tap(({ showToast }) => {
        if (showToast) {
          const errorMessage = this._translationService.instant('general.creation_error') + ' ' + this._translationService.instant('handover_protocol');
          this._snackbarService.showSnackbar(
            errorMessage,
            'mat-warn',
            true
          );
        }
      })
    ),
    { dispatch: false }
  );

  public trySignAndCompleteHandoverProtocol$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DataActions.SignAndCompleteHandoverProtocol),
      mergeMap(({ handoverProtocolSignature }) => this._hopApiFacade.signAndCompleteHandoverProtocol$(
        handoverProtocolSignature.objectId,
        handoverProtocolSignature.handoverProtocolId,
        handoverProtocolSignature.transferorsSignatureData,
        handoverProtocolSignature.transfereesSignatureData,
      ).pipe(
        tap((handoverProtocol) => {
          if (!environment.staticToggles.showNewHopScreen) {
            this._documentService.openDocumentLink(handoverProtocol.signatureData.signedDocumentUrl);
          }

          this._router.navigateByUrl(`/objects/${handoverProtocol.objectId}/handover-protocols/${handoverProtocol.id}/email`);
        }),
        catchError(() => {
          this._router.navigateByUrl(`/objects/${handoverProtocolSignature.objectId}/handover-protocols`);

          return EMPTY;
        }),
      )),
    ),
    { dispatch: false }
  );

  public draftSignHandoverProtocol$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DataActions.DraftSignHandoverProtocol),
      mergeMap(({ handoverProtocolSignature, openEmailDialog }) =>
        this._hopApiFacade.signAndCompleteHandoverProtocol$(
          handoverProtocolSignature.objectId,
          handoverProtocolSignature.handoverProtocolId,
          handoverProtocolSignature.transferorsSignatureData,
          handoverProtocolSignature.transfereesSignatureData,
        ).pipe(
          tap((handoverProtocol) => {
            if (openEmailDialog) {
              this._router.navigateByUrl(`/objects/${handoverProtocol.objectId}/handover-protocols/${handoverProtocol.id}/email`);
            }
          }),
          catchError(() => {
            this._router.navigateByUrl(`/objects/${handoverProtocolSignature.objectId}/handover-protocols`);

            return EMPTY;
          }),
        )),
    ),
    { dispatch: false }
  );


  public sendHandoverProtocolEmail$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DataActions.SendHandoverProtocolEmail),
      switchMap(({ objectId, handoverProtocolId, emailData }) =>
        this._hopApiFacade.sendHandoverProtocolEmail$(objectId, handoverProtocolId, emailData).pipe(
          tap((success) => {
            if (success) {
              const successMessage = this._translationService.instant('handover_protocol') + ' ' + this._translationService.instant('general.email_sent_successfully');
              this._snackbarService.showSnackbar(
                successMessage,
                'mat-primary',
                true
              );
            } else {
              throwError(new Error());
            }
          }),
          catchError(() => {
            const errorMessage = this._translationService.instant('handover_protocol') + ' ' + this._translationService.instant('general.email_sent_failure');
            this._snackbarService.showSnackbar(
              errorMessage,
              'mat-warn',
              true
            );

            return [];
          })
        )
      )
    ),
    { dispatch: false }
  );

  constructor(
    private readonly _actions$: Actions,
    private readonly _router: Router,
    private readonly _documentService: DocumentService,
    private readonly _snackbarService: SnackbarService,
    private readonly _hopApiFacade: HandoverProtocolApiFacade,
    private readonly _hopDataFacade: HandoverProtocolDataFacade,
    private readonly _documentDataFacade: DocumentsDataFacade,
    private readonly _localHandoverProtocolService: LocalHandoverProtocolService,
    private readonly _networkStatusService: NetworkStatusService,
    private _translationService: TranslationService
  ) { }
}
